import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Play, Pause, Square, Plus, Minus } from 'lucide-react';
import { doc, onSnapshot, updateDoc, collection, addDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

const MatchPage = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);

  // Player stats state
  const [playerStats, setPlayerStats] = useState({
    team1Player1: { aces: 0, unforcedErrors: 0, kickSmashes: 0, akhlaaqRating: 3 },
    team1Player2: { aces: 0, unforcedErrors: 0, kickSmashes: 0, akhlaaqRating: 3 },
    team2Player1: { aces: 0, unforcedErrors: 0, kickSmashes: 0, akhlaaqRating: 3 },
    team2Player2: { aces: 0, unforcedErrors: 0, kickSmashes: 0, akhlaaqRating: 3 }
  });

  // Match score state
  const [matchScore, setMatchScore] = useState({
    team1Sets: 0,
    team2Sets: 0,
    currentSet: 1,
    team1Games: 0,
    team2Games: 0,
    team1Points: 0,
    team2Points: 0
  });

  useEffect(() => {
    if (matchId) {
      const matchRef = doc(db, 'matches', matchId);
      const unsubscribe = onSnapshot(matchRef, (doc) => {
        if (doc.exists()) {
          const matchData = { id: doc.id, ...doc.data() };
          setMatch(matchData);
          setIsLive(matchData.status === 'live');
          
          if (matchData.playerStats) {
            setPlayerStats(matchData.playerStats);
          }
          if (matchData.score) {
            setMatchScore(matchData.score);
          }
        }
        setLoading(false);
      });

      return unsubscribe;
    }
  }, [matchId]);

  const updatePlayerStat = (player, stat, value) => {
    const newStats = {
      ...playerStats,
      [player]: {
        ...playerStats[player],
        [stat]: Math.max(0, playerStats[player][stat] + value)
      }
    };
    setPlayerStats(newStats);
    updateMatchData({ playerStats: newStats });
  };

  const updateAkhlaaqRating = (player, rating) => {
    const newStats = {
      ...playerStats,
      [player]: {
        ...playerStats[player],
        akhlaaqRating: rating
      }
    };
    setPlayerStats(newStats);
    updateMatchData({ playerStats: newStats });
  };

  const updateMatchData = async (data) => {
    if (matchId) {
      try {
        await updateDoc(doc(db, 'matches', matchId), {
          ...data,
          updatedAt: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error updating match:', error);
      }
    }
  };

  const startMatch = async () => {
    await updateMatchData({
      status: 'live',
      startTime: new Date().toISOString()
    });
    setIsLive(true);
  };

  const endMatch = async () => {
    await updateMatchData({
      status: 'completed',
      endTime: new Date().toISOString()
    });
    setIsLive(false);
  };

  const updateScore = (team, type, value) => {
    const newScore = { ...matchScore };
    
    if (type === 'points') {
      newScore[`team${team}Points`] = Math.max(0, newScore[`team${team}Points`] + value);
    } else if (type === 'games') {
      newScore[`team${team}Games`] = Math.max(0, newScore[`team${team}Games`] + value);
    } else if (type === 'sets') {
      newScore[`team${team}Sets`] = Math.max(0, newScore[`team${team}Sets`] + value);
    }
    
    setMatchScore(newScore);
    updateMatchData({ score: newScore });
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="loading">Loading match...</div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-4">Match not found</h2>
          <button
            onClick={() => navigate('/fixtures')}
            className="btn btn-primary"
          >
            Back to Fixtures
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={() => navigate('/fixtures')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft size={20} />
          Back to Fixtures
        </button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2" style={{ color: '#1C563D' }}>
              {match.team1} vs {match.team2}
            </h1>
            <p className="text-gray-600">
              {new Date(match.date).toLocaleDateString()} at {match.time}
            </p>
          </div>
          
          <div className="flex gap-2">
            {!isLive ? (
              <button
                onClick={startMatch}
                className="btn btn-primary flex items-center gap-2"
              >
                <Play size={20} />
                Start Match
              </button>
            ) : (
              <button
                onClick={endMatch}
                className="btn btn-secondary flex items-center gap-2"
              >
                <Square size={20} />
                End Match
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Live Score Display */}
      {isLive && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card mb-6 bg-red-50 border border-red-200"
        >
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="w-3 h-3 bg-red-500 rounded-full mr-2 animate-pulse"></div>
              <span className="text-red-600 font-bold text-lg">LIVE MATCH</span>
            </div>
            
            <div className="grid grid-cols-2 gap-8">
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2">{match.team1}</h3>
                <div className="text-4xl font-bold" style={{ color: '#1C563D' }}>
                  {matchScore.team1Sets}
                </div>
                <div className="text-sm text-gray-600">Sets</div>
              </div>
              
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2">{match.team2}</h3>
                <div className="text-4xl font-bold" style={{ color: '#1C563D' }}>
                  {matchScore.team2Sets}
                </div>
                <div className="text-sm text-gray-600">Sets</div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Team 1 Player Stats */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#1C563D' }}>
            {match.team1} - Player Stats
          </h2>
          
          <div className="space-y-6">
            {['team1Player1', 'team1Player2'].map((playerKey, index) => (
              <div key={playerKey} className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold mb-3">
                  Player {index + 1}
                </h3>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span>Aces:</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updatePlayerStat(playerKey, 'aces', -1)}
                        className="btn btn-sm btn-secondary"
                        disabled={!isLive}
                      >
                        <Minus size={16} />
                      </button>
                      <span className="font-bold text-lg">{playerStats[playerKey].aces}</span>
                      <button
                        onClick={() => updatePlayerStat(playerKey, 'aces', 1)}
                        className="btn btn-sm btn-primary"
                        disabled={!isLive}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span>Unforced Errors:</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updatePlayerStat(playerKey, 'unforcedErrors', -1)}
                        className="btn btn-sm btn-secondary"
                        disabled={!isLive}
                      >
                        <Minus size={16} />
                      </button>
                      <span className="font-bold text-lg">{playerStats[playerKey].unforcedErrors}</span>
                      <button
                        onClick={() => updatePlayerStat(playerKey, 'unforcedErrors', 1)}
                        className="btn btn-sm btn-primary"
                        disabled={!isLive}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span>Kick Smashes:</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updatePlayerStat(playerKey, 'kickSmashes', -1)}
                        className="btn btn-sm btn-secondary"
                        disabled={!isLive}
                      >
                        <Minus size={16} />
                      </button>
                      <span className="font-bold text-lg">{playerStats[playerKey].kickSmashes}</span>
                      <button
                        onClick={() => updatePlayerStat(playerKey, 'kickSmashes', 1)}
                        className="btn btn-sm btn-primary"
                        disabled={!isLive}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span>Akhlaaq Rating:</span>
                    <div className="flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          onClick={() => updateAkhlaaqRating(playerKey, rating)}
                          className={`w-8 h-8 rounded-full text-sm font-bold ${
                            playerStats[playerKey].akhlaaqRating === rating
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-200 text-gray-600'
                          }`}
                          disabled={!isLive}
                        >
                          {rating}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Team 2 Player Stats */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#1C563D' }}>
            {match.team2} - Player Stats
          </h2>
          
          <div className="space-y-6">
            {['team2Player1', 'team2Player2'].map((playerKey, index) => (
              <div key={playerKey} className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold mb-3">
                  Player {index + 1}
                </h3>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span>Aces:</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updatePlayerStat(playerKey, 'aces', -1)}
                        className="btn btn-sm btn-secondary"
                        disabled={!isLive}
                      >
                        <Minus size={16} />
                      </button>
                      <span className="font-bold text-lg">{playerStats[playerKey].aces}</span>
                      <button
                        onClick={() => updatePlayerStat(playerKey, 'aces', 1)}
                        className="btn btn-sm btn-primary"
                        disabled={!isLive}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span>Unforced Errors:</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updatePlayerStat(playerKey, 'unforcedErrors', -1)}
                        className="btn btn-sm btn-secondary"
                        disabled={!isLive}
                      >
                        <Minus size={16} />
                      </button>
                      <span className="font-bold text-lg">{playerStats[playerKey].unforcedErrors}</span>
                      <button
                        onClick={() => updatePlayerStat(playerKey, 'unforcedErrors', 1)}
                        className="btn btn-sm btn-primary"
                        disabled={!isLive}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span>Kick Smashes:</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updatePlayerStat(playerKey, 'kickSmashes', -1)}
                        className="btn btn-sm btn-secondary"
                        disabled={!isLive}
                      >
                        <Minus size={16} />
                      </button>
                      <span className="font-bold text-lg">{playerStats[playerKey].kickSmashes}</span>
                      <button
                        onClick={() => updatePlayerStat(playerKey, 'kickSmashes', 1)}
                        className="btn btn-sm btn-primary"
                        disabled={!isLive}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span>Akhlaaq Rating:</span>
                    <div className="flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          onClick={() => updateAkhlaaqRating(playerKey, rating)}
                          className={`w-8 h-8 rounded-full text-sm font-bold ${
                            playerStats[playerKey].akhlaaqRating === rating
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-200 text-gray-600'
                          }`}
                          disabled={!isLive}
                        >
                          {rating}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MatchPage;